import { Component, effect, inject, OnInit } from '@angular/core';
import { Title } from "@angular/platform-browser";
import { ActivatedRoute, Router } from '@angular/router';
import { EntityType } from '@models/common/entity-type.enum';
import { RelationshipIndividualDto } from "@models/individuals/relationship-individual-dto";
import {
  NgbDropdown,
  NgbDropdownItem,
  NgbDropdownMenu,
  NgbDropdownToggle,
  NgbTooltip
} from '@ng-bootstrap/ng-bootstrap';
import {
  ContactsComponent,
  IContactsChangeEvent
} from "@pages/individuals/components/individuals-page/components/contacts/contacts.component";
import { IndividualProfileGridService } from "@pages/individuals/services/individual-profile-grid.service";
import { IndividualProfileStateService } from "@pages/individuals/services/individual-profile-state.service";
import { AlertsListComponent } from "@shared/components/alerts-list/alerts-list.component";
import { CommonPageComponent } from "@shared/components/commons/common-page/common-page.component";
import { DividerComponent } from "@shared/components/commons/divider/divider.component";
import { LoaderComponent } from "@shared/components/commons/loader/loader.component";
import {
  EntityProfileTagsComponent
} from "@shared/components/entity-profiles/entity-profile-tags/entity-profile-tags.component";
import {
  XpmCardProfileComponent
} from "@shared/components/entity-profiles/xpm-card-profile/xpm-card-profile.component";
import {
  GridClientSideComponent
} from "@shared/components/grid/components/grid-client-side/grid-client-side.component";
import { NotesComponent } from "@shared/components/notes/notes.component";
import { TabButtonComponent } from "@shared/controls/tab-button/tab-button.component";
import { ToggleButtonComponent } from "@shared/controls/toggle-button/toggle-button.component";
import { HasRoleDirective } from "@shared/directives/has-role.directive";
import { DateFormatPipe } from "@shared/pipes/dateFormatPipe";
import { DefaulValuePipe } from "@shared/pipes/defaultValuePipe";
import { DinPipe } from "@shared/pipes/din.pipe";
import { PhonePipe } from "@shared/pipes/phonePipe";
import { getPageTitle } from "@shared/utils/get-page-title";
import { Guid } from "@shared/utils/guid.helper";
import { GridApi, IsFullWidthRowParams } from "ag-grid-community";
import { GridOptions, RowHeightParams } from "ag-grid-enterprise";
import {
  AgIndividualRelationshipActionsComponent
} from "./components/ag-individual-relationship-actions/ag-individual-relationship-actions.component";
import {
  IndividualEntityDividerComponent
} from "./components/individual-entity-divider/individual-entity-divider.component";
import {
  IndividualIdVerificationComponent
} from "./components/individual-id-verification/individual-id-verification.component";
import { UIIndividualWarningStates } from "./constants/ui-individual-warning-states";
import { IndividualRelationshipGroupModel } from "./models/individual-relationship-group-model";

@Component({
  selector: 'app-individual',
  standalone: true,
  templateUrl: 'individual-profile-page.component.html',
  styleUrl: 'individual-profile-page.component.scss',
  imports: [
    DefaulValuePipe,
    PhonePipe,
    DateFormatPipe,
    DinPipe,
    LoaderComponent,
    EntityProfileTagsComponent,
    TabButtonComponent,
    DividerComponent,
    NotesComponent,
    AlertsListComponent,
    HasRoleDirective,
    ContactsComponent,
    NgbDropdown,
    NgbDropdownToggle,
    NgbDropdownMenu,
    NgbDropdownItem,
    NgbTooltip,
    XpmCardProfileComponent,
    ToggleButtonComponent,
    IndividualIdVerificationComponent,
    GridClientSideComponent
  ],
  providers: [
    IndividualProfileStateService,
    IndividualProfileGridService
  ],
})
export class IndividualProfilePageComponent extends CommonPageComponent implements OnInit {
  private individualProfileStateService = inject(IndividualProfileStateService);
  private individualProfileGridService = inject(IndividualProfileGridService);

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private titleService = inject(Title);

  readonly EntityType = EntityType;
  readonly entityType = EntityType.Individual;
  readonly UIIndividualWarningStates = UIIndividualWarningStates;
  readonly differentProfileCardValuesMessage = 'Most of your entities have different values, Click to do something';

  individualDto = this.individualProfileStateService.state.individualDto;
  individualRelationshipsViewModel = this.individualProfileStateService.individualRelationshipsViewModel;

  contactsViewModel = this.individualProfileStateService.contactsViewModel;
  notesViewModel = this.individualProfileStateService.notesViewModel;
  alertsViewModel = this.individualProfileStateService.alertsViewModel;

  isIndividualLoading = this.individualProfileStateService.state.loaders.isIndividualLoading;
  isControlsDisabled = this.individualProfileStateService.isControlsDisabled;
  isXpmConnected = this.individualProfileStateService.state.isXpmConnected;
  currentProviderConnectionStatus = this.individualProfileStateService.state.currentProviderConnectionStatus;
  warningStates = this.individualProfileStateService.warningStates;
  placeOfBirth = this.individualProfileStateService.placeOfBirth;
  organisationSettings = this.individualProfileStateService.state.organisationSettings;

  constructor() {
    super();

    effect(() => {
      const individualName = this.individualDto().fullNameLabel;

      if (individualName) {
        this.titleService.setTitle(getPageTitle(individualName));
        this.headerService.setHeaderData({
          title: individualName,
          subtitle: 'Individual',
          iconClass: 'ic-user-20',
          backPath: ['/individuals']
        });

        this.setColDefs();
      }
    });
  }

  override ngOnInit() {
    super.ngOnInit();

    const individualId = this.route.snapshot.paramMap.get('id');

    if (!individualId || !Guid.isValidGuid(individualId)) {
      this.router.navigate(['/individuals']);
      return;
    }

    this.individualProfileStateService.init(individualId);
  }

  setIsPastOfficersVisible(isPastOfficersVisible: boolean): void {
    this.individualProfileStateService.setIsPastOfficersVisible(isPastOfficersVisible);
  }

  setContactVisible(isVisible = true): void {
    this.individualProfileStateService.patchContactsState({ isVisible });
  }

  onContactsChanges(event: IContactsChangeEvent): void {
    this.individualProfileStateService.patchIndividualContacts(event);
  }

  setNotesVisible(isVisible = true): void {
    this.individualProfileStateService.patchContactsState({ isVisible });
  }

  onNotesTotalChange(difference: number): void {
    const total = this.notesViewModel().total + difference;
    this.individualProfileStateService.patchNotesState({ total });
  }

  setAlertsVisible(isVisible: boolean): void {
    this.individualProfileStateService.patchAlertsState({ isVisible });
  }

  loadESignProvidersStatus(): void {
    this.individualProfileStateService.loadESignProvidersStatus();
  }

  // =====  Modals  =====

  openUpdateAddressModal(): void {
    this.individualProfileStateService.openUpdateAddressModal();
  }

  openUpdateDateOfBirthModal(): void {
    this.individualProfileStateService.openUpdateDateOfBirthModal();
  }

  openUpdateBirthPlaceModal(): void {
    this.individualProfileStateService.openUpdateBirthPlaceModal();
  }

  openEditDinModal(): void {
    this.individualProfileStateService.openEditDinModal();
  }

  openEditFamilyGroupModal(): void {
    this.individualProfileStateService.openEditFamilyGroupModal();
  }

  openEditReferenceNumberModal(): void {
    this.individualProfileStateService.openEditReferenceNumberModal();
  }

  openAssignIndividualToExistingIndividualModal(relationships: RelationshipIndividualDto[] = this.checkedIndividualRelationships): void {
    this.individualProfileStateService.openAssignIndividualToExistingIndividualModal(relationships);
  }

  splitSelectedRelationshipToNewIndividual(relationships: RelationshipIndividualDto[] = this.checkedIndividualRelationships): void {
    this.individualProfileStateService.splitSelectedRelationshipToNewIndividual(relationships);
  }

  openAddContactModal(): void {
    this.individualProfileStateService.openAddContactModal();
  }

  openUpdateRelationshipContactDetails(relationships: RelationshipIndividualDto[] = this.checkedIndividualRelationships): void {
    return this.individualProfileStateService.openUpdateRelationshipContactDetails(relationships);
  }

  openSelectBulkChangeAddressMethodModal(relationships: RelationshipIndividualDto[] = this.checkedIndividualRelationships): void {
    this.individualProfileStateService.openSelectBulkChangeAddressMethodModal(relationships);
  }

  // =====  Forms  =====

  openCompanyChangePartyNameModal(relationship: RelationshipIndividualDto): void {
    this.individualProfileStateService.openCompanyChangePartyNameModal(relationship);
  }

  // =====  Bulk Forms  =====

  openCompanyMistakeCorrectionModal(relationship: RelationshipIndividualDto): void {
    this.individualProfileStateService.openCompanyMistakeCorrectionModal(relationship);
  }

  openCompanyChangePartyNameModalBulk(relationships: RelationshipIndividualDto[] = this.checkedIndividualRelationships): void {
    return this.individualProfileStateService.openCompanyChangePartyNameModalBulk(relationships);
  }

  openCompanyChangeAddressForm(groupModel: IndividualRelationshipGroupModel): void {
    this.individualProfileStateService.openCompanyChangeAddressForm(groupModel);
  }

  // =====  Grid  =====

  readonly gridOptions: GridOptions = {
    isFullWidthRow: (params: IsFullWidthRowParams<unknown>): boolean => typeof params.rowNode.data == 'string',
    getRowHeight: (params: RowHeightParams) => typeof params.data == 'string' ? 24 : 64,
    fullWidthCellRenderer: IndividualEntityDividerComponent
  };

  checkedIndividualRelationships: RelationshipIndividualDto[] = [];

  colDefs = this.individualProfileGridService.colDefs;

  onGridReady(gridApi: GridApi): void {
    this.individualProfileGridService.setGridApi(gridApi);
  }

  onIndividualSelect(groupModels: (IndividualRelationshipGroupModel | string)[]): void {
    // The data includes full width row with string
    // so we have to filter them
    this.checkedIndividualRelationships = (groupModels
      .filter((groupModel) => typeof groupModel !== 'string') as IndividualRelationshipGroupModel[])
      .flatMap((groupModel) => groupModel.relationshipsDtos);
  }

  setColDefs(): void {
    const actionColDef = {
      sortable: false,
      minWidth: 90,
      width: 90,
      flex: 0,
      cellRenderer: AgIndividualRelationshipActionsComponent,
      cellRendererParams: {
        onUpdateContactDetails: (groupModel: IndividualRelationshipGroupModel) => this.openUpdateRelationshipContactDetails(groupModel.relationshipsDtos),
        splitIndividualsToNew: (groupModel: IndividualRelationshipGroupModel) => this.splitSelectedRelationshipToNewIndividual(groupModel.relationshipsDtos),
        onAssignIndividualsToExisting: (groupModel: IndividualRelationshipGroupModel) => this.openAssignIndividualToExistingIndividualModal(groupModel.relationshipsDtos),
        onChangeName: (groupModel: IndividualRelationshipGroupModel) => this.openCompanyChangePartyNameModal(groupModel.relationshipsDtos[0]),
        onChangeAddress: (groupModel: IndividualRelationshipGroupModel) => this.openCompanyChangeAddressForm(groupModel),
        onMistakeCorrection: (groupModel: IndividualRelationshipGroupModel) => this.openCompanyMistakeCorrectionModal(groupModel.relationshipsDtos[0]),
      }
    };

    this.individualProfileGridService.setColDefsWithIndividualDtoAndActions(
      actionColDef,
      this.individualDto(),
      this.openCompanyChangeAddressForm.bind(this)
    );
  }

  // =====  Export  =====

  onExportIndividuals(): void {
    console.log('onExportIndividuals: ');
  }
}
